import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {
    setAttachmentsDetails,
    setPageTempAttachments,
    SetPageTempAttachmentsData as SetPageTempAttachmentsDataPayload,
    removeTempAttachmentById,
    removeTempAttachment,
    RemoveTempAttachmentData as RemoveTempAttachmentPayload,
} from './utils';

import {getAttachments, deleteAttachment, attachFiles} from './thunks';

import {AttachmentsState} from './types';

const initialState: AttachmentsState = {
    entities: {},
    temporary: {},
};

interface ResetPayload {
    pageId: number;
}

interface SetPageTempAttachmentIdPayload {
    id: number;
    uid: string;
    pageId: number;
}

interface SetTempAttachmentThumbnailPayload {
    thumbnail: string;
    uid: string;
    pageId: number;
}

const {reducer, actions} = createSlice({
    name: 'attachments',
    initialState,
    reducers: {
        reset(state, _action: PayloadAction<ResetPayload>) {
            state.entities = {};
        },
        setTempAttachments(state, {payload}: PayloadAction<SetPageTempAttachmentsDataPayload>) {
            const {pageId, temporary} = payload;

            setPageTempAttachments(state, {
                pageId,
                temporary,
            });
        },
        setTempAttachmentId(state, {payload}: PayloadAction<SetPageTempAttachmentIdPayload>) {
            const {pageId, id, uid} = payload;
            const entity = state.temporary[pageId];

            if (!entity) {
                return;
            }

            const item = entity.find((attach) => attach.uid === uid);

            if (item) {
                Object.assign(item, {id});
            }
        },
        setTempAttachmentThumbnail(
            state,
            {payload}: PayloadAction<SetTempAttachmentThumbnailPayload>,
        ) {
            const {pageId, uid, thumbnail} = payload;
            const entity = state.temporary[pageId];

            if (!entity) {
                return;
            }

            const item = entity.find((attach) => attach.uid === uid);

            if (item) {
                Object.assign(item, {thumbnail});
            }
        },
        removeTempAttachment(state, {payload}: PayloadAction<RemoveTempAttachmentPayload>) {
            const {pageId, temp} = payload;

            removeTempAttachment(state, {pageId, temp});
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(getAttachments.fulfilled, (state, {payload}) => {
                payload.results.forEach((attachment) => {
                    setAttachmentsDetails(state, attachment);
                });
            })
            .addCase(deleteAttachment.fulfilled, (state, action) => {
                const {pageId, attachmentId} = action.meta.arg;

                delete state.entities[attachmentId];
                removeTempAttachmentById(state, {pageId, attachmentId});
            })
            .addCase(attachFiles.fulfilled, (state, action) => {
                action.payload.forEach((attachment) => setAttachmentsDetails(state, attachment));
            }),
});

export {actions, reducer};
